import type { FC } from "react";

import moment from "moment";
import { Helmet } from "react-helmet";

import { Badge, classNames, TimeClock, Tooltip } from "@alpacahq/ui/old";

import { getPrettyCash, getPrettyDate } from "src/v2/utils/formatting";

interface ProgramInterestProps {
  estimatedInterest: number;
  stocksLent: number;
  lendingsInProgress: number;
  latestLoanDate?: string;
  loading?: boolean;
}

export const ProgramInterestPres: FC<ProgramInterestProps> = ({
  estimatedInterest,
  stocksLent,
  lendingsInProgress,
  latestLoanDate,
  loading = false,
}: ProgramInterestProps) => {
  const prettyEstimatedInterest = getPrettyCash(estimatedInterest, {
    forcePrecision: 2,
    minimumFractionDigits: 2,
  });

  const displayDate = latestLoanDate
    ? getPrettyDate(moment(latestLoanDate).toDate().getTime(), "MMM DD, YYYY")
    : "TBD";

  return (
    <>
      <Helmet title="Alpaca – Program Interest" />
      <div className="flex flex-col gap-4 py-6">
        <div className="flex items-center gap-2 py-2">
          <h1 className="m-0 font-semibold text-gray-800 text-lg">
            Stock Lending
          </h1>
          <Badge variant="v2/gray" className="flex items-center gap-1">
            <TimeClock size="sm" />
            <span className="font-medium text-gray-600 text-xs">
              Last Updated: {displayDate}
            </span>
          </Badge>
        </div>
        <div className="flex flex-row gap-5">
          <div className="flex h-[90px] w-[216px] flex-col justify-center gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 pt-3.5 pb-4 [&>button]:self-start [&>button]:text-left">
            <Tooltip content="Estimated interest is updated daily after markets close. In some cases, updates may take up to 48h.">
              <span className="font-semibold text-gray-700 text-sm underline decoration-[14%] decoration-dotted underline-offset-2">
                Est. Interest
              </span>
            </Tooltip>
            <div className="relative flex h-7 items-center">
              <div
                className={classNames(
                  "absolute h-7 w-24 animate-pulse rounded bg-gray-200 transition-opacity duration-500 ease-in-out",
                  loading ? "opacity-100" : "opacity-0",
                )}
              />
              <span
                className={classNames(
                  "font-medium font-mono text-xl transition-opacity duration-500 ease-in-out",
                  loading ? "opacity-0" : "opacity-100",
                  estimatedInterest === 0 && "text-gray-500",
                  estimatedInterest < 0 && "text-red-500",
                  estimatedInterest > 0 && "text-emerald-500",
                )}
              >
                {estimatedInterest === 0 ? "-" : prettyEstimatedInterest}
              </span>
            </div>
          </div>
          <div className="flex h-[90px] w-[216px] flex-col justify-center gap-3 rounded-lg border border-gray-300 bg-gray-50 p-4">
            <span className="font-semibold text-gray-800">
              Lendings in Progress
            </span>
            <div className="relative flex h-6 items-center">
              <div
                className={classNames(
                  "absolute h-6 w-16 animate-pulse rounded bg-gray-200 transition-opacity duration-500 ease-in-out",
                  loading ? "opacity-100" : "opacity-0",
                )}
              />
              <span
                className={classNames(
                  "font-mono font-normal text-base text-gray-700 transition-opacity duration-500 ease-in-out",
                  loading ? "opacity-0" : "opacity-100",
                )}
              >
                {lendingsInProgress || "-"}
              </span>
            </div>
          </div>
          <div className="flex h-[90px] w-[216px] flex-col justify-center gap-3 rounded-lg border border-gray-300 bg-gray-50 p-4">
            <span className="font-semibold text-gray-800">Lendings Closed</span>
            <div className="relative flex h-6 items-center">
              <div
                className={classNames(
                  "absolute h-6 w-16 animate-pulse rounded bg-gray-200 transition-opacity duration-500 ease-in-out",
                  loading ? "opacity-100" : "opacity-0",
                )}
              />
              <span
                className={classNames(
                  "font-mono font-normal text-base text-gray-700 transition-opacity duration-500 ease-in-out",
                  loading ? "opacity-0" : "opacity-100",
                )}
              >
                {stocksLent - lendingsInProgress || "-"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgramInterestPres;
